import syncApi, { baseURLValue, subscribeToJobStream } from './syncApi';

/*
 * PIM API client. Reuses the shared `syncApi` axios instance so the
 * X-Sync-API-Key / X-User headers and global 401/503 handling apply
 * uniformly across the console.
 */

// ── Field-type + context ──
export const listFieldTypes = () => syncApi.get('/pim/field-types').then((r) => r.data);
export const listChannels = () => syncApi.get('/pim/channels').then((r) => r.data);
export const listLocales = () => syncApi.get('/pim/locales').then((r) => r.data);

// ── Attribute groups ──
export const listGroups = () => syncApi.get('/pim/groups').then((r) => r.data);
export const createGroup = (payload) => syncApi.post('/pim/groups', payload).then((r) => r.data);
export const updateGroup = (id, payload) => syncApi.put(`/pim/groups/${id}`, payload).then((r) => r.data);
export const deleteGroup = (id) => syncApi.delete(`/pim/groups/${id}`).then((r) => r.data);

// ── Attributes ──
export const listAttributes = () => syncApi.get('/pim/attributes').then((r) => r.data);
export const getAttribute = (code) =>
  syncApi.get(`/pim/attributes/${encodeURIComponent(code)}`).then((r) => r.data);
export const getAttributeSourceValues = (code) =>
  syncApi.get(`/pim/attributes/${encodeURIComponent(code)}/source-values`).then((r) => r.data);
export const createAttribute = (payload) => syncApi.post('/pim/attributes', payload).then((r) => r.data);
export const updateAttribute = (id, version, payload) =>
  syncApi
    .put(`/pim/attributes/${id}`, payload, { headers: { 'If-Match': String(version) } })
    .then((r) => r.data);
export const deleteAttribute = (id) => syncApi.delete(`/pim/attributes/${id}`).then((r) => r.data);
export const replaceAttributeOptions = (id, options) =>
  syncApi.put(`/pim/attributes/${id}/options`, { options }).then((r) => r.data);

// ── Families ──
export const listFamilies = () => syncApi.get('/pim/families').then((r) => r.data);
export const getFamily = (code) =>
  syncApi.get(`/pim/families/${encodeURIComponent(code)}`).then((r) => r.data);
export const getFamilyTemplate = (code) =>
  syncApi.get(`/pim/families/${encodeURIComponent(code)}/template`).then((r) => r.data);
export const createFamily = (payload) => syncApi.post('/pim/families', payload).then((r) => r.data);
export const updateFamily = (id, version, payload) =>
  syncApi
    .put(`/pim/families/${id}`, payload, { headers: { 'If-Match': String(version) } })
    .then((r) => r.data);
export const deleteFamily = (id) => syncApi.delete(`/pim/families/${id}`).then((r) => r.data);
export const replaceFamilyAttributes = (id, attributes) =>
  syncApi.put(`/pim/families/${id}/attributes`, { attributes }).then((r) => r.data);

// ── Products (Phase 3) ──
export const listProducts = (params) => syncApi.get('/pim/products', { params }).then((r) => r.data);
export const getProduct = (sku, params) =>
  syncApi.get(`/pim/products/${encodeURIComponent(sku)}`, { params }).then((r) => r.data);
export const createProduct = (payload) => syncApi.post('/pim/products', payload).then((r) => r.data);
export const saveProductValues = (sku, values) =>
  syncApi.put(`/pim/products/${encodeURIComponent(sku)}/values`, { values }).then((r) => r.data);
export const setProductPublication = (sku, channelId, payload) =>
  syncApi
    .put(`/pim/products/${encodeURIComponent(sku)}/publication/${channelId}`, payload)
    .then((r) => r.data);
export const getProductHistory = (sku) =>
  syncApi.get(`/pim/products/${encodeURIComponent(sku)}/history`).then((r) => r.data);
export const recomputeCompleteness = (sku) =>
  syncApi.post(`/pim/products/${encodeURIComponent(sku)}/recompute`).then((r) => r.data);

// ── Media (Phase 3) ──
export const uploadAsset = (file, onProgress) => {
  const form = new FormData();
  form.append('file', file);
  return syncApi
    .post('/pim/assets', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    })
    .then((r) => r.data);
};
export const listProductAssets = (sku, attributeCode) =>
  syncApi
    .get(`/pim/products/${encodeURIComponent(sku)}/assets`, { params: { attribute_code: attributeCode } })
    .then((r) => r.data);
export const setProductAssets = (sku, attributeCode, assetIds, scope = {}) =>
  syncApi
    .put(`/pim/products/${encodeURIComponent(sku)}/assets`, {
      attribute_code: attributeCode,
      asset_ids: assetIds,
      channel_id: scope.channel_id ?? 0,
      locale_id: scope.locale_id ?? 0,
    })
    .then((r) => r.data);
export const assetUrl = (assetId) => `${baseURLValue}/pim/assets/${assetId}/raw`;

// ── Media library (browse / tag / reuse) ──
export const listAssets = (params) =>
  syncApi.get('/pim/assets', { params }).then((r) => r.data);
export const listAssetTags = () => syncApi.get('/pim/asset-tags').then((r) => r.data);
export const updateAsset = (id, payload) =>
  syncApi.patch(`/pim/assets/${id}`, payload).then((r) => r.data);

// ── Mappings (Phase 2 + 4) ──
export const listSourceMappings = () => syncApi.get('/pim/mappings/source').then((r) => r.data);
export const upsertSourceMapping = (payload) =>
  syncApi.post('/pim/mappings/source', payload).then((r) => r.data);
export const deleteSourceMapping = (id) =>
  syncApi.delete(`/pim/mappings/source/${id}`).then((r) => r.data);
export const listDestinationMappings = () =>
  syncApi.get('/pim/mappings/destination').then((r) => r.data);
export const upsertDestinationMapping = (payload) =>
  syncApi.post('/pim/mappings/destination', payload).then((r) => r.data);
export const deleteDestinationMapping = (id) =>
  syncApi.delete(`/pim/mappings/destination/${id}`).then((r) => r.data);
export const previewMapping = (sku) =>
  syncApi.get('/pim/mappings/preview', { params: { sku } }).then((r) => r.data);

// ── Enrichment (SerpAPI + Gemini) ──
const enrichBaseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Preview runs SerpAPI + Gemini inline and can take 20–40s; override the
// shared 30s axios timeout so a slow generation doesn't surface as a failure.
export const previewEnrich = (sku) =>
  syncApi.post('/pim/enrich/preview', { sku }, { timeout: 120000 }).then((r) => r.data);

export const commitEnrich = (payload) =>
  syncApi.post('/pim/enrich/commit', payload, { timeout: 60000 }).then((r) => r.data);

export const createEnrichJob = (payload) =>
  syncApi.post('/pim/enrich/jobs', payload).then((r) => r.data);

export const listEnrichJobs = (params) =>
  syncApi.get('/pim/enrich/jobs', { params }).then((r) => r.data);

export const getEnrichJob = (id) =>
  syncApi.get(`/pim/enrich/jobs/${id}`).then((r) => r.data);

export const cancelEnrichJob = (id) =>
  syncApi.post(`/pim/enrich/jobs/${id}/cancel`).then((r) => r.data);

export const subscribeToEnrichJobStream = (id, onProgress, onClose) =>
  subscribeToJobStream(`${enrichBaseURL}/pim/enrich/jobs/${id}/stream`, onProgress, onClose);

// ── Structured name rebuild (component parse + review/apply) ──
export const listNameRebuild = (params) =>
  syncApi.get('/pim/name-rebuild', { params }).then((r) => r.data);

export const createNameRebuildJob = (payload) =>
  syncApi.post('/pim/name-rebuild/jobs', payload).then((r) => r.data);

export const reviewNameRebuild = (payload) =>
  syncApi.post('/pim/name-rebuild/review', payload).then((r) => r.data);

export const applyNameRebuild = (payload) =>
  syncApi.post('/pim/name-rebuild/apply', payload, { timeout: 120000 }).then((r) => r.data);

// ── Store-tree auto-categorisation (rules + AI fallback) ──
export const getCategorisationOverview = () =>
  syncApi.get('/pim/categorisation/overview').then((r) => r.data);

export const listCategorisationRules = (params) =>
  syncApi.get('/pim/categorisation/rules', { params }).then((r) => r.data);

export const createCategorisationRule = (payload) =>
  syncApi.post('/pim/categorisation/rules', payload).then((r) => r.data);

export const updateCategorisationRule = (id, payload) =>
  syncApi.patch(`/pim/categorisation/rules/${id}`, payload).then((r) => r.data);

export const deleteCategorisationRule = (id) =>
  syncApi.delete(`/pim/categorisation/rules/${id}`).then((r) => r.data);

export const seedCategorisationRules = () =>
  syncApi.post('/pim/categorisation/rules/seed', {}, { timeout: 120000 }).then((r) => r.data);

// Preview may call Gemini (+ SerpAPI) inline; allow a slow response.
export const previewCategorisation = (sku, ai = true) =>
  syncApi
    .get(`/pim/categorisation/preview/${encodeURIComponent(sku)}`, {
      params: { ai }, timeout: 120000,
    })
    .then((r) => r.data);

export const createCategorisationJob = (payload) =>
  syncApi.post('/pim/categorisation/jobs', payload).then((r) => r.data);

export const getCategorisationLog = (params) =>
  syncApi.get('/pim/categorisation/log', { params }).then((r) => r.data);

export const revertCategorisationJob = (jobId) =>
  syncApi.post(`/pim/categorisation/revert/${jobId}`, {}, { timeout: 300000 }).then((r) => r.data);

export const syncBcStoreCategories = (payload) =>
  syncApi.post('/pim/categorisation/bc-category-sync', payload, { timeout: 300000 }).then((r) => r.data);

// ── AI image generation (SerpAPI reference + Gemini blue-studio redraw) ──
// Preview searches + generates inline and can take 30–90s.
export const previewImage = (sku, referenceUrl) =>
  syncApi
    .post('/pim/images/preview', { sku, reference_url: referenceUrl || null }, { timeout: 180000 })
    .then((r) => r.data);

// SerpAPI-only search (fast, no Gemini call) for the pick-first flow.
export const searchImageReferences = (sku) =>
  syncApi.get('/pim/images/search', { params: { sku }, timeout: 60000 }).then((r) => r.data);

export const previewImageUpload = (sku, file) => {
  const form = new FormData();
  form.append('sku', sku);
  form.append('file', file);
  return syncApi
    .post('/pim/images/preview-upload', form, {
      timeout: 180000,
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((r) => r.data);
};

export const commitImage = (candidateId, destination = 'primary_image') =>
  syncApi
    .post('/pim/images/commit', { candidate_id: candidateId, destination }, { timeout: 60000 })
    .then((r) => r.data);

export const rejectImage = (candidateId) =>
  syncApi.post('/pim/images/reject', { candidate_id: candidateId }).then((r) => r.data);

export const createImageJob = (payload) =>
  syncApi.post('/pim/images/jobs', payload).then((r) => r.data);

export const listImageCandidates = (params) =>
  syncApi.get('/pim/images/candidates', { params }).then((r) => r.data);

export const imageCandidateUrl = (candidateId) =>
  `${baseURLValue}/pim/images/candidates/${candidateId}/raw`;

export const imageCandidateReferenceUrl = (candidateId) =>
  `${baseURLValue}/pim/images/candidates/${candidateId}/reference`;

// ── Interim PIM → Akeneo product push ──
export const listAkeneoPushProducts = (params) =>
  syncApi.get('/pim/akeneo-push/products', { params }).then((r) => r.data);

// The push is synchronous: up to MAX_PUSH_BATCH SKUs, each a GET + PATCH plus
// an optional media upload, so it needs far more than the shared 30s timeout.
export const pushToAkeneo = (payload) =>
  syncApi.post('/pim/akeneo-push/push', payload, { timeout: 180000 }).then((r) => r.data);

export const previewAkeneoPush = (sku) =>
  syncApi
    .get(`/pim/akeneo-push/preview/${encodeURIComponent(sku)}`, { timeout: 60000 })
    .then((r) => r.data);

// ── Category trees + assignments ──
export const listCategoryTrees = () =>
  syncApi.get('/pim/categories/trees').then((r) => r.data);
export const createCategoryTree = (payload) =>
  syncApi.post('/pim/categories/trees', payload).then((r) => r.data);
export const updateCategoryTree = (code, payload) =>
  syncApi.patch(`/pim/categories/trees/${encodeURIComponent(code)}`, payload).then((r) => r.data);
export const deleteCategoryTree = (code) =>
  syncApi.delete(`/pim/categories/trees/${encodeURIComponent(code)}`).then((r) => r.data);
export const setCategoryTreeChannels = (code, channelIds) =>
  syncApi
    .put(`/pim/categories/trees/${encodeURIComponent(code)}/channels`, { channel_ids: channelIds })
    .then((r) => r.data);
export const getCategoryTreeNodes = (code) =>
  syncApi.get(`/pim/categories/trees/${encodeURIComponent(code)}/nodes`).then((r) => r.data);
export const createCategoryNode = (payload) =>
  syncApi.post('/pim/categories', payload).then((r) => r.data);
export const updateCategoryNode = (id, payload) =>
  syncApi.patch(`/pim/categories/${id}`, payload).then((r) => r.data);
export const deleteCategoryNode = (id, cascade = false) =>
  syncApi.delete(`/pim/categories/${id}`, { params: { cascade } }).then((r) => r.data);
export const listCategoryProducts = (id, params) =>
  syncApi.get(`/pim/categories/${id}/products`, { params }).then((r) => r.data);
export const assignCategoryProducts = (id, { add = [], remove = [] } = {}) =>
  syncApi.post(`/pim/categories/${id}/assign`, { add, remove }).then((r) => r.data);
export const getProductCategories = (sku) =>
  syncApi.get(`/pim/products/${encodeURIComponent(sku)}/categories`).then((r) => r.data);
export const setProductCategories = (sku, categoryIds, treeCode = null) =>
  syncApi
    .put(`/pim/products/${encodeURIComponent(sku)}/categories`, {
      category_ids: categoryIds,
      tree_code: treeCode,
    })
    .then((r) => r.data);

// ── PIM general settings (internal-product rule + enrichment locale groups) ──
export const getInternalRule = () =>
  syncApi.get('/pim/settings/internal-rule').then((r) => r.data);
export const updateInternalRule = (payload) =>
  syncApi.put('/pim/settings/internal-rule', payload).then((r) => r.data);
export const getLocaleGroups = () =>
  syncApi.get('/pim/settings/locale-groups').then((r) => r.data);
export const updateLocaleGroups = (groups) =>
  syncApi.put('/pim/settings/locale-groups', { groups }).then((r) => r.data);
export const listClassifications = () =>
  syncApi.get('/pim/settings/classifications').then((r) => r.data);
